import {
  RecentSearch,
  RECENT_SEARCH_KEY_PREFIX,
  addRecentSearch,
  parseRecentSearches,
  recentSearchStorageKey,
} from './globalSearchContract';

// The only place the palette touches localStorage. Kept apart from globalSearchContract so that module
// stays import-free and unit-testable in the node environment (see FRONTEND.md §14), and free of the auth
// dependency: the user id is PASSED IN rather than read here, so the key-building rule stays testable.
//
// EVERY access is wrapped. localStorage is not merely "sometimes empty": in a private window, with site
// data blocked, or during a thumbnail/preview render, the accessor itself THROWS. An unguarded read here
// would take the whole palette down on open, and this is a convenience feature - it must fail invisibly.

/**
 * Removes the pre-user-keyed list, which was shared by everyone using the browser profile. One line, and
 * it runs at most once per browser - without it the old shared list simply sits there forever, which is
 * the leak this keying exists to close.
 */
function removeLegacySharedList(): void {
  try {
    window.localStorage.removeItem(RECENT_SEARCH_KEY_PREFIX);
  } catch (error) {
    // Storage unavailable; nothing to clean up.
  }
}

export function readRecentSearches(userId: number | string | null | undefined): RecentSearch[] {
  const key = recentSearchStorageKey(userId);

  // No user, no list. Deliberately not falling back to a shared key.
  if (key === null) return [];

  removeLegacySharedList();

  try {
    return parseRecentSearches(window.localStorage.getItem(key));
  } catch (error) {
    return [];
  }
}

/**
 * Remembers one search and returns the new list, so the caller can render it without a second read.
 * Returns the unchanged list if storage is unavailable — the palette then behaves as if the feature is
 * simply off, which is the correct degradation for a per-browser convenience.
 */
export function rememberRecentSearch(userId: number | string | null | undefined, entry: RecentSearch): RecentSearch[] {
  const key = recentSearchStorageKey(userId);

  if (key === null) return [];

  const updated = addRecentSearch(readRecentSearches(userId), entry);

  try {
    window.localStorage.setItem(key, JSON.stringify(updated));
  } catch (error) {
    // Quota exceeded, or storage blocked. The in-memory list is still correct for this session.
  }

  return updated;
}

export function clearRecentSearches(userId: number | string | null | undefined): RecentSearch[] {
  const key = recentSearchStorageKey(userId);

  try {
    if (key !== null) window.localStorage.removeItem(key);
    removeLegacySharedList();
  } catch (error) {
    // Nothing to do — the caller clears its own state regardless.
  }

  return [];
}
